'use client';

import { useToast } from '@/components/ui/use-toast';
import { ErrorHandler } from '@/lib/ErrorHandler';
import {
  claimBannerPromo,
  isBannerGenerateCode,
  loginUrlForBannerClaim,
  needsLoginForBannerClaim,
  stashBannerClaim,
} from '@/lib/bannerPromo';
import { cdnLcpUrl, LCP_DESKTOP_MEDIA, LCP_DESKTOP_WIDTH, LCP_MOBILE_WIDTH } from '@/lib/lcpImage';
import { HomePageBanner } from '@/models/HomePage';
import { differenceInSeconds, parseISO } from 'date-fns';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { memo, useEffect, useState, type MouseEvent } from 'react';

const Countdown = dynamic(() => import('./Countdown'), {
  ssr: false,
  loading: () => null,
});

interface OptimizedBannerViewProps {
  inCarousel: boolean;
  index: number;
  banner?: HomePageBanner;
  isFirstBanner?: boolean;
  isPriority?: boolean;
  isLCP?: boolean;
  /** Desktop: fill parent height (object-cover) instead of fixed aspect ratio. */
  fillHeight?: boolean;
  /** Partner landing: full image on mobile, fill 12/5 box on desktop. Homepage stays cropped. */
  objectFit?: 'cover' | 'contain';
  desktopWidth?: number;
}

const OptimizedBannerView = ({
  banner,
  isPriority = false,
  fillHeight = false,
  objectFit = 'cover',
  desktopWidth = LCP_DESKTOP_WIDTH,
}: OptimizedBannerViewProps) => {
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showCountdown, setShowCountdown] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const { toast } = useToast();
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const raw = banner?.badgeEndTime;
    if (!raw) return setEndDate(null);
    try {
      const d = parseISO(raw);
      const left = differenceInSeconds(d, new Date());
      setEndDate(left > 10 ? d : null);
    } catch {
      setEndDate(null);
    }
  }, [banner?.badgeEndTime]);

  useEffect(() => {
    if (!endDate) return;
    const t = setTimeout(() => setShowCountdown(true), 200);
    return () => clearTimeout(t);
  }, [endDate]);

  const altText = banner?.alt || banner?.title || 'kampanya görseli';
  const desktopImage = banner?.imageUrl || '';
  const mobileImage = banner?.mobileImageUrl || desktopImage;
  const mobileSrc = cdnLcpUrl(mobileImage, LCP_MOBILE_WIDTH);
  const desktopSrc = cdnLcpUrl(desktopImage || mobileImage, desktopWidth);
  const generateCode = isBannerGenerateCode(banner?.generateCode);
  const href = banner?.url || '';
  const squeeze = objectFit === 'contain';

  const goToBannerUrl = () => {
    const next = String(href || '').trim();
    if (!next) return;
    router.push(next.startsWith('/') ? next : `/${next}`);
  };

  const onBannerClick = async (e: MouseEvent) => {
    if (!generateCode || !banner?.id) return;
    e.preventDefault();
    if (claiming) return;
    if (needsLoginForBannerClaim()) {
      stashBannerClaim({ id: banner.id, url: href }, pathname || '/');
      router.push(loginUrlForBannerClaim());
      return;
    }
    setClaiming(true);
    try {
      const code = await claimBannerPromo(banner.id);
      if (code) {
        toast({ description: `Kampanya kodunuz: ${code}` });
      } else {
        toast({ variant: 'destructive', description: 'Şu an kod verilemiyor' });
      }
      goToBannerUrl();
    } catch (error) {
      toast({
        variant: 'destructive',
        description: ErrorHandler.getMessage(error).message || 'Kod alınamadı',
      });
    } finally {
      setClaiming(false);
    }
  };

  if (!banner?.badgeEndTime || endDate) {
    const className = `block w-full relative ${fillHeight ? 'md:h-full' : ''}${claiming ? ' pointer-events-none' : ''}`;
    const inner = (
      <>
        <div
          className={`relative w-full overflow-hidden md:rounded-2xl ${
            squeeze
              ? 'min-[600px]:aspect-[12/5]'
              : `aspect-[430/242] min-[600px]:aspect-[12/5] ${
                  fillHeight ? 'md:aspect-auto md:absolute md:inset-0 md:h-full md:min-h-0' : ''
                }`
          }`}
        >
          {mobileSrc ? (
            <picture className={squeeze ? 'contents' : undefined}>
              {desktopSrc ? <source media={LCP_DESKTOP_MEDIA} srcSet={desktopSrc} /> : null}
              {/* Native <img> + <picture>: Next/Image srcset ignores media and pulled both DPR slots. */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={mobileSrc}
                alt={altText}
                width={430}
                height={242}
                sizes="100vw"
                decoding={isPriority ? 'sync' : 'async'}
                loading={isPriority ? 'eager' : 'lazy'}
                fetchPriority={isPriority ? 'high' : 'low'}
                className={
                  squeeze
                    ? 'w-full h-auto min-[600px]:absolute min-[600px]:inset-0 min-[600px]:h-full min-[600px]:w-full min-[600px]:object-cover min-[600px]:object-center md:rounded-2xl'
                    : `w-full h-auto md:rounded-2xl ${
                        fillHeight
                          ? 'min-[600px]:absolute min-[600px]:inset-0 min-[600px]:h-full min-[600px]:object-cover'
                          : ''
                      }`
                }
              />
            </picture>
          ) : null}
        </div>

        {showCountdown && endDate && (
          <div className="absolute bottom-14 left-10 max-sm:bottom-0 max-sm:left-4">
            <Countdown endDate={endDate} transformOrigin="bottom left" hideHeader={true} />
          </div>
        )}
      </>
    );

    if (generateCode && !href) {
      return (
        <button type="button" className={className} onClick={onBannerClick}>
          {inner}
        </button>
      );
    }

    return (
      <Link href={href || '/'} prefetch={false} className={className} onClick={onBannerClick}>
        {inner}
      </Link>
    );
  }

  return null;
};

export default memo(OptimizedBannerView);
